import { makeAutoObservable } from 'mobx'
import { logger } from 'shared/lib'
import { SingletonRequest } from 'shared/api/lib'
import {
  type Integration,
  type IIntegrationKey,
  type IMergeFieldType,
  IntegrationKey,
  IntegrationsApi,
} from 'entities/Integrations'
import type { IChatbotField } from 'entities/Chatbot'
import { getCustomTypeByData } from 'entities/MergeField/lib/getCustomTypeByData'
import { toChatbotFieldType } from 'entities/MergeField/lib/toChatbotFieldType'
import { CustomField } from 'entities/CustomField'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { MergeField } from 'entities/MergeField/model/MergeField'
import { mockMergeField } from 'entities/MergeField/mock/MergeFieldMock'

export type ISelectedProperty = {
  id: string
  type: IMergeFieldType
  customField?: CustomField
  integration?: Integration
  label?: string
  default: boolean
  required?: boolean
  choices: string[]
}

export class BookingFieldsStore {
  private _fieldsMap: Map<string, IChatbotField> = new Map()
  private _selectedPropertiesMap: Map<string, ISelectedProperty> = new Map()

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)
  }

  private _fetchFields = async ({
    id,
    integrationKey,
  }: {
    id: string
    integrationKey: IIntegrationKey
  }) => {
    try {
      this.dispose()

      const isHubspotKey = integrationKey === IntegrationKey.hubspot

      const { data } = isHubspotKey
        ? await IntegrationsApi.getHubspotIntegrationMeetingsFields(integrationKey, {
            id: encodeURIComponent(id),
          })
        : await IntegrationsApi.getCalendlyIntegrationMeetingsFields(integrationKey, {
            id: id,
          })

      const fields = data.additional_fields

      fields.forEach((item) => {
        const type = getCustomTypeByData(item.type)

        const chatbotField = {
          key: item.key,
          name: item.name,
          type: toChatbotFieldType(item.type),
          settings: {
            default: item.default,
            required: item.required,
            choices: item.choices,
          },
        }

        const customField = new CustomField({
          key: chatbotField.key,
          name: chatbotField.name,
          type: type,
          visible: true,
          editable: true,
          category_new: 'custom',
        })

        const integration = this._entities.integrationsStore.getIntegration(integrationKey)

        this._fieldsMap.set(item.key, chatbotField)

        if (integration) {
          this._selectedPropertiesMap.set(item.key, {
            id: chatbotField.key,
            type: chatbotField.type,
            customField: customField,
            integration: integration,
            label: chatbotField.name,
            default: chatbotField.settings?.default || false,
            required: chatbotField.settings?.required || false,
            choices: chatbotField.settings?.choices || [],
          })
        }
      })
    } catch (e) {
      logger.error(e)
    }
  }

  fetchFields = new SingletonRequest(this._fetchFields).request

  dispose = () => {
    this._fieldsMap.clear()
    this._selectedPropertiesMap.clear()
  }

  get fields() {
    return Array.from(this._fieldsMap.values())
  }

  get selectedProperties() {
    return Array.from(this._selectedPropertiesMap.values())
  }

  private _toMergeFields = (excludeHubspotDefaults = false): MergeField[] =>
    this.selectedProperties.reduce<MergeField[]>((state, item) => {
      const { customField: field, integration } = item

      if (excludeHubspotDefaults && item.default && integration?.key === IntegrationKey.hubspot)
        return state
      if (field && integration)
        state.push(
          new MergeField({
            ...mockMergeField,
            integrationId: integration.id,
            integrationKey: integration.key,
            label: field.name,
            name: field.key,
            type: item.type,
            template: field.template,
          })
        )
      return state
    }, [])

  get allMergeFields(): MergeField[] {
    return this._toMergeFields()
  }

  get hubspotMergeFields(): MergeField[] {
    return this._toMergeFields(true)
  }
}
